import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import API_BASE_URL from '../config';
import '../styles/AdminDashboard.css';

const MAIN_TABS = [
  { key: 'moderation', label: 'Moderation', icon: '👥' },
  { key: 'analytics', label: 'Analytics', icon: '📊' },
];

const STATUS_TABS = [
  { key: 'pending', label: 'Pending' },
  { key: 'approved', label: 'Approved' },
  { key: 'denied', label: 'Denied' },
  { key: 'all', label: 'All' },
];

const ANALYTICS_TABS = [
  { key: 'technicians', label: 'Technicians' },
  { key: 'hirers', label: 'Hirers' },
  { key: 'hires', label: 'Hires' },
  { key: 'suspended', label: 'Suspended' },
  { key: 'reports', label: 'Reports' },
  { key: 'support', label: 'Contact Messages' },
  { key: 'support-tickets', label: 'Support Chat' },
];

function AdminDashboard() {
  const navigate = useNavigate();
  const [mainTab, setMainTab] = useState('moderation');
  const [modView, setModView] = useState('technicians');
  const [statusTab, setStatusTab] = useState('pending');
  const [analyticsTab, setAnalyticsTab] = useState('technicians');
  
  const [technicians, setTechnicians] = useState([]);
  const [hirers, setHirers] = useState([]);
  const [hires, setHires] = useState([]);
  const [suspended, setSuspended] = useState([]);
  const [reports, setReports] = useState([]);
  const [supportMessages, setSupportMessages] = useState([]);
  const [supportTickets, setSupportTickets] = useState([]);
  const [pendingCategories, setPendingCategories] = useState([]);
  const [selectedSupportTicket, setSelectedSupportTicket] = useState(null);
  const [ticketMessages, setTicketMessages] = useState([]);
  const [adminReplyText, setAdminReplyText] = useState('');
  const [replyLoading, setReplyLoading] = useState(false);
  const [loadingTicketMsgs, setLoadingTicketMsgs] = useState(false);
  
  // Report management
  const [selectedReport, setSelectedReport] = useState(null);
  const [reportStatusLoading, setReportStatusLoading] = useState(false);
  
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const [previewTech, setPreviewTech] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const [deleteError, setDeleteError] = useState('');

  const token = localStorage.getItem('token');

  const fetchTechnicians = useCallback(async (statusFilter) => {
    setLoading(true);
    try {
      const res = await fetch(
        `${API_BASE_URL}/admin/technicians?status_filter=${statusFilter}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!res.ok) {
        if (res.status === 401 || res.status === 403) {
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          navigate('/admin');
          return;
        }
        return;
      }
      const data = await res.json();
      setTechnicians(data.technicians || []);
      setTotal(data.total || 0);
    } catch {
      console.error('Failed to fetch technicians');
    } finally {
      setLoading(false);
    }
  }, [token, navigate]);

  const fetchPendingCategories = useCallback(async (statusFilter = 'pending') => {
    try {
      const res = await fetch(`${API_BASE_URL}/admin/categories?status_filter=${statusFilter}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setPendingCategories(data.categories || []);
      }
    } catch {
      console.error('Failed to fetch pending categories');
    }
  }, [token]);

  const updateReportStatus = async (reportId, newStatus) => {
    setReportStatusLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/admin/reports/${reportId}/status`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ status: newStatus }),
      });
      
      if (res.ok) {
        fetchAnalytics('reports');
        setSelectedReport(null);
      } else {
        const data = await res.json();
        alert(data.detail || 'Failed to update report status');
      }
    } catch (err) {
      alert('Network error. Please try again.');
    } finally {
      setReportStatusLoading(false);
    }
  };

  const handleCategoryAction = async (categoryId, action) => {
    setActionLoading(`cat-${categoryId}`);
    try {
      const res = await fetch(
        `${API_BASE_URL}/admin/categories/${categoryId}/${action}`,
        { method: 'POST', headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.ok) {
        fetchPendingCategories(statusTab);
      }
    } catch {
      console.error(`Failed to ${action} category request`);
    } finally {
      setActionLoading(null);
    }
  };

  const fetchAnalytics = useCallback(async (tab) => {
    setLoading(true);
    try {
      let endpoint = '';
      switch (tab) {
        case 'technicians':
          endpoint = '/admin/technicians?status_filter=all';
          break;
        case 'hirers':
          endpoint = '/admin/analytics/hirers';
          break;
        case 'hires':
          endpoint = '/admin/analytics/completed-hires';
          break;
        case 'suspended':
          endpoint = '/admin/analytics/suspended';
          break;
        case 'reports':
          endpoint = '/admin/analytics/reports';
          break;
        case 'support':
          endpoint = '/admin/analytics/support-messages';
          break;
        case 'support-tickets':
          endpoint = '/admin/support/tickets?status_filter=all';
          break;
        default:
          return;
      }

      const res = await fetch(`${API_BASE_URL}${endpoint}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!res.ok) {
        if (res.status === 401 || res.status === 403) {
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          navigate('/admin');
          return;
        }
        return;
      }

      const data = await res.json();
      
      switch (tab) {
        case 'technicians':
          setTechnicians(data.technicians || []);
          setTotal(data.total || 0);
          break;
        case 'hirers':
          setHirers(data.hirers || []);
          setTotal(data.total || 0);
          break;
        case 'hires':
          setHires(data.hires || []);
          setTotal(data.total || 0);
          break;
        case 'suspended':
          setSuspended(data.suspended || []);
          setTotal(data.total || 0);
          break;
        case 'reports':
          setReports(data.reports || []);
          setTotal(data.total || 0);
          break;
        case 'support':
          setSupportMessages(data.messages || []);
          setTotal(data.total || 0);
          break;
        case 'support-tickets':
          setSupportTickets(data.tickets || []);
          setTotal(data.total || 0);
          break;
        default:
          break;
      }
    } catch {
      console.error('Failed to fetch analytics');
    } finally {
      setLoading(false);
    }
  }, [token, navigate]);

  useEffect(() => {
    if (mainTab === 'moderation') {
      if (modView === 'technicians') {
        fetchTechnicians(statusTab);
      } else {
        fetchPendingCategories(statusTab);
      }
    } else if (mainTab === 'analytics') {
      fetchAnalytics(analyticsTab);
    }
  }, [mainTab, modView, statusTab, analyticsTab, fetchTechnicians, fetchPendingCategories, fetchAnalytics]);

  const handleAction = async (techId, action) => {
    setActionLoading(techId);
    try {
      const res = await fetch(
        `${API_BASE_URL}/admin/technicians/${techId}/${action}`,
        { method: 'POST', headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.ok) {
        fetchTechnicians(statusTab);
        if (previewTech && previewTech.id === techId) setPreviewTech(null);
      }
    } catch {
      console.error(`Failed to ${action} technician`);
    } finally {
      setActionLoading(null);
    }
  };

  const handleDelete = async (userId, userRole) => {
    setActionLoading(userId);
    setDeleteError('');
    try {
      const endpoint = userRole === 'technician' 
        ? `/admin/technicians/${userId}`
        : `/admin/hirers/${userId}`;
      
      const res = await fetch(`${API_BASE_URL}${endpoint}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      
      if (res.ok) {
        setDeleteConfirm(null);
        setDeleteError('');
        if (mainTab === 'moderation') {
          fetchTechnicians(statusTab);
        } else {
          fetchAnalytics(analyticsTab);
        }
      } else {
        const data = await res.json();
        setDeleteError(data.detail || 'Failed to delete. User may have related records (hires, ratings, messages).');
      }
    } catch (err) {
      setDeleteError('Network error. Please try again.');
    } finally {
      setActionLoading(null);
    }
  };

  const handleSignOut = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/');
  };

  const fetchSupportTicketDetail = async (ticketId) => {
    setLoadingTicketMsgs(true);
    try {
      const res = await fetch(`${API_BASE_URL}/admin/support/ticket/${ticketId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setSelectedSupportTicket(data);
        setTicketMessages(data.messages || []);
      }
    } catch {
      console.error('Failed to fetch ticket detail');
    } finally {
      setLoadingTicketMsgs(false);
    }
  };

  const handleAdminReply = async (e) => {
    e.preventDefault();
    if (!adminReplyText.trim() || !selectedSupportTicket) return;
    setReplyLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/admin/support/ticket/${selectedSupportTicket.id}/reply`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ content: adminReplyText.trim() }),
      });
      if (res.ok) {
        setAdminReplyText('');
        await fetchSupportTicketDetail(selectedSupportTicket.id);
        fetchAnalytics('support-tickets');
      }
    } catch {
      console.error('Failed to send reply');
    } finally {
      setReplyLoading(false);
    }
  };

  const handleCloseTicket = async (ticketId) => {
    try {
      const res = await fetch(`${API_BASE_URL}/admin/support/ticket/${ticketId}/close`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        setSelectedSupportTicket((prev) => prev ? { ...prev, status: 'closed' } : prev);
        fetchAnalytics('support-tickets');
      }
    } catch {
      console.error('Failed to close ticket');
    }
  };

  const displayType = (t) =>
    t.tech_type === 'other' ? (t.custom_tech_type || 'Other') : (t.tech_type || '—');

  const certUrl = (path) => path ? `${API_BASE_URL}/uploads/${path}` : null;

  return (
    <div className="ad-shell">
      <header className="ad-header">
        <span className="ad-header__logo">SkillArea</span>
        <span className="ad-header__label">Admin Dashboard</span>
        <button className="ad-header__logout" onClick={handleSignOut}>Sign Out</button>
      </header>

      <div className="ad-body">
        {/* Main Tabs */}
        <div className="ad-main-tabs">
          {MAIN_TABS.map((t) => (
            <button
              key={t.key}
              className={`ad-main-tab${mainTab === t.key ? ' ad-main-tab--active' : ''}`}
              onClick={() => setMainTab(t.key)}
            >
              <span className="ad-main-tab__icon">{t.icon}</span>
              <span className="ad-main-tab__label">{t.label}</span>
            </button>
          ))}
        </div>

        {/* Moderation Section */}
        {mainTab === 'moderation' && (
          <>
            {/* ── Sub-view toggle ── */}
            <div className="ad-mod-toggle">
              <button
                className={`ad-mod-toggle__btn${modView === 'technicians' ? ' ad-mod-toggle__btn--active' : ''}`}
                onClick={() => { setModView('technicians'); setStatusTab('pending'); }}
              >
                Technicians
              </button>
              <button
                className={`ad-mod-toggle__btn${modView === 'categories' ? ' ad-mod-toggle__btn--active' : ''}`}
                onClick={() => { setModView('categories'); setStatusTab('pending'); }}
              >
                Category Requests
              </button>
            </div>

            {/* ── Shared status filter tabs ── */}
            <div className="ad-tabs">
              {STATUS_TABS.map((t) => (
                <button
                  key={t.key}
                  className={`ad-tab${statusTab === t.key ? ' ad-tab--active' : ''}`}
                  onClick={() => setStatusTab(t.key)}
                >
                  {t.label}
                </button>
              ))}
            </div>

            {/* ── Technicians sub-view ── */}
            {modView === 'technicians' && (
              <>
                <p className="ad-count">{total} technician{total !== 1 ? 's' : ''}</p>

                {loading ? (
                  <div className="ad-loading">Loading...</div>
                ) : technicians.length === 0 ? (
                  <div className="ad-empty">No {statusTab === 'all' ? '' : statusTab} technicians found.</div>
                ) : (
                  <div className="ad-list">
                    {technicians.map((t) => (
                      <div className="ad-card" key={t.id}>
                        <div className="ad-card__top">
                          <div>
                            <p className="ad-card__name">{t.full_name}</p>
                            <p className="ad-card__meta">
                              {displayType(t)} &middot; {t.city}, {t.province}, {t.country}
                            </p>
                            <p className="ad-card__meta">{t.email} &middot; {t.phone}</p>
                          </div>
                          <span className={`ad-badge ad-badge--${t.status}`}>{t.status}</span>
                        </div>

                        <div className="ad-card__actions">
                          <button
                            className="ad-btn ad-btn--outline"
                            onClick={() => setPreviewTech(t)}
                          >
                            Review
                          </button>
                          {t.status !== 'approved' && (
                            <button
                              className="ad-btn ad-btn--approve"
                              disabled={actionLoading === t.id}
                              onClick={() => handleAction(t.id, 'approve')}
                            >
                              {actionLoading === t.id ? '...' : 'Approve'}
                            </button>
                          )}
                          {t.status !== 'denied' && (
                            <button
                              className="ad-btn ad-btn--deny"
                              disabled={actionLoading === t.id}
                              onClick={() => handleAction(t.id, 'deny')}
                            >
                              {actionLoading === t.id ? '...' : 'Deny'}
                            </button>
                          )}
                          <button
                            className="ad-btn ad-btn--delete"
                            onClick={() => setDeleteConfirm({ id: t.id, name: t.full_name, role: 'technician' })}
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}

            {/* ── Category Requests sub-view ── */}
            {modView === 'categories' && (
              <section className="ad-table ad-table--category">
                <div className="ad-section-head">
                  <h3 className="ad-section-title">
                    {statusTab === 'all' ? 'All' : `${statusTab.charAt(0).toUpperCase()}${statusTab.slice(1)}`} Category Requests
                  </h3>
                  <p className="ad-section-subtitle">Review technician category applications and approve supporting documents.</p>
                </div>
                {pendingCategories.length === 0 ? (
                  <div className="ad-empty">No {statusTab === 'all' ? '' : statusTab} category requests.</div>
                ) : (
                  <table>
                    <thead>
                      <tr>
                        <th>Technician</th>
                        <th>Category</th>
                        <th>Document</th>
                        <th>Status</th>
                        <th>Submitted</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pendingCategories.map((c) => (
                        <tr key={c.id}>
                          <td>{c.technician_name || '—'}</td>
                          <td className="ad-capitalize">{c.custom_category || c.category}</td>
                          <td>
                            {c.certificate_path ? (
                              <a
                                href={certUrl(c.certificate_path)}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="ad-cert-link"
                              >
                                View document
                              </a>
                            ) : '—'}
                          </td>
                          <td>
                            <span className={`ad-badge ad-badge--${c.status}`}>{c.status}</span>
                          </td>
                          <td>{c.created_at ? new Date(c.created_at).toLocaleDateString() : '—'}</td>
                          <td>
                            {c.status !== 'approved' && (
                              <button
                                className="ad-btn ad-btn--approve ad-btn--sm"
                                disabled={actionLoading === `cat-${c.id}`}
                                onClick={() => handleCategoryAction(c.id, 'approve')}
                              >
                                Approve
                              </button>
                            )}
                            {c.status !== 'denied' && (
                              <button
                                className="ad-btn ad-btn--deny ad-btn--sm"
                                disabled={actionLoading === `cat-${c.id}`}
                                onClick={() => handleCategoryAction(c.id, 'deny')}
                              >
                                Deny
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </section>
            )}
          </>
        )}

        {/* Analytics Section */}
        {mainTab === 'analytics' && (
          <>
            <div className="ad-tabs">
              {ANALYTICS_TABS.map((t) => (
                <button
                  key={t.key}
                  className={`ad-tab${analyticsTab === t.key ? ' ad-tab--active' : ''}`}
                  onClick={() => setAnalyticsTab(t.key)}
                >
                  {t.label}
                </button>
              ))}
            </div>

            <p className="ad-count">{total} {analyticsTab === 'support' ? 'message' : analyticsTab === 'support-tickets' ? 'ticket' : 'record'}{total !== 1 ? 's' : ''}</p>

            {loading ? (
              <div className="ad-loading">Loading...</div>
            ) : (
              <div className="ad-analytics">
                {/* Technicians Analytics */}
                {analyticsTab === 'technicians' && (
                  <div className="ad-table">
                    {technicians.length === 0 ? (
                      <div className="ad-empty">No technicians found.</div>
                    ) : (
                      <table>
                        <thead>
                          <tr>
                            <th>Name</th>
                            <th>Email</th>
                            <th>Trade</th>
                            <th>Status</th>
                            <th>Registered</th>
                          </tr>
                        </thead>
                        <tbody>
                          {technicians.map((t) => (
                            <tr key={t.id}>
                              <td>{t.full_name}</td>
                              <td>{t.email}</td>
                              <td>{displayType(t)}</td>
                              <td><span className={`ad-badge ad-badge--${t.status}`}>{t.status}</span></td>
                              <td>{t.created_at ? new Date(t.created_at).toLocaleDateString() : '—'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                )}

                {/* Hirers Analytics */}
                {analyticsTab === 'hirers' && (
                  <div className="ad-table">
                    {hirers.length === 0 ? (
                      <div className="ad-empty">No hirers found.</div>
                    ) : (
                      <table>
                        <thead>
                          <tr>
                            <th>Name</th>
                            <th>Email</th>
                            <th>Company</th>
                            <th>Type</th>
                            <th>Registered</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {hirers.map((h) => (
                            <tr key={h.id}>
                              <td>{h.full_name}</td>
                              <td>{h.email}</td>
                              <td>{h.company_name || '—'}</td>
                              <td>{h.is_individual ? 'Individual' : 'Company'}</td>
                              <td>{h.created_at ? new Date(h.created_at).toLocaleDateString() : '—'}</td>
                              <td>
                                <button
                                  className="ad-btn ad-btn--delete ad-btn--sm"
                                  onClick={() => setDeleteConfirm({ id: h.id, name: h.full_name, role: 'hirer' })}
                                >
                                  Delete
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                )}

                {/* Hires Analytics */}
                {analyticsTab === 'hires' && (
                  <div className="ad-table">
                    {hires.length === 0 ? (
                      <div className="ad-empty">No hires found.</div>
                    ) : (
                      <table>
                        <thead>
                          <tr>
                            <th>Hirer</th>
                            <th>Technician</th>
                            <th>Status</th>
                            <th>Hired On</th>
                            <th>Completed On</th>
                          </tr>
                        </thead>
                        <tbody>
                          {hires.map((h) => (
                            <tr key={h.id}>
                              <td>{h.hirer_name}</td>
                              <td>{h.technician_name}</td>
                              <td>
                                <span className={`ad-status-pill ad-status-pill--${h.status === 'completed' ? 'approved' : 'pending'}`}>
                                  {h.status === 'completed' ? 'Completed' : 'Active'}
                                </span>
                              </td>
                              <td>{h.created_at ? new Date(h.created_at).toLocaleDateString() : '—'}</td>
                              <td>{h.completed_at ? new Date(h.completed_at).toLocaleDateString() : '—'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                )}

                {/* Suspended Accounts */}
                {analyticsTab === 'suspended' && (
                  <div className="ad-table">
                    {suspended.length === 0 ? (
                      <div className="ad-empty">No suspended accounts found.</div>
                    ) : (
                      <table>
                        <thead>
                          <tr>
                            <th>Name</th>
                            <th>Email</th>
                            <th>Role</th>
                            <th>Reason</th>
                            <th>Suspended</th>
                          </tr>
                        </thead>
                        <tbody>
                          {suspended.map((s) => (
                            <tr key={s.id}>
                              <td>{s.user_name}</td>
                              <td>{s.user_email}</td>
                              <td className="ad-capitalize">{s.user_role}</td>
                              <td>{s.reason}</td>
                              <td>{s.suspended_at ? new Date(s.suspended_at).toLocaleDateString() : '—'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                )}

                {/* Reports */}
                {analyticsTab === 'reports' && (
                  <div className="ad-reports-layout">
                    <div className="ad-reports-list">
                      {reports.length === 0 ? (
                        <div className="ad-empty">No reports found.</div>
                      ) : (
                        <table>
                          <thead>
                            <tr>
                              <th>Reporter</th>
                              <th>Reported User</th>
                              <th>Reason</th>
                              <th>Status</th>
                              <th>Date</th>
                              <th>Action</th>
                            </tr>
                          </thead>
                          <tbody>
                            {reports.map((r) => (
                              <tr key={r.id}>
                                <td>{r.reporter_name} ({r.reporter_role})</td>
                                <td>{r.reported_name} ({r.reported_role})</td>
                                <td>{r.reason}</td>
                                <td><span className={`ad-badge ad-badge--${r.status}`}>{r.status}</span></td>
                                <td>{r.created_at ? new Date(r.created_at).toLocaleDateString() : '—'}</td>
                                <td>
                                  <button 
                                    className="ad-btn ad-btn--small"
                                    onClick={() => setSelectedReport(r)}
                                  >
                                    View Details
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                    </div>
                    
                    {selectedReport && (
                      <div className="ad-report-detail">
                        <div className="ad-report-detail-header">
                          <h3>Report Details</h3>
                          <button 
                            className="ad-btn ad-btn--ghost"
                            onClick={() => setSelectedReport(null)}
                          >
                            ×
                          </button>
                        </div>
                        
                        <div className="ad-report-detail-content">
                          <div className="ad-report-field">
                            <strong>Reporter:</strong> {selectedReport.reporter_name} ({selectedReport.reporter_role})
                          </div>
                          <div className="ad-report-field">
                            <strong>Reported User:</strong> {selectedReport.reported_name} ({selectedReport.reported_role})
                          </div>
                          <div className="ad-report-field">
                            <strong>Reason:</strong> {selectedReport.reason}
                          </div>
                          <div className="ad-report-field">
                            <strong>Description:</strong>
                            <p>{selectedReport.description || 'No description provided'}</p>
                          </div>
                          <div className="ad-report-field">
                            <strong>Current Status:</strong> 
                            <span className={`ad-badge ad-badge--${selectedReport.status}`}>{selectedReport.status}</span>
                          </div>
                          <div className="ad-report-field">
                            <strong>Date:</strong> {selectedReport.created_at ? new Date(selectedReport.created_at).toLocaleDateString() : '—'}
                          </div>
                        </div>
                        
                        <div className="ad-report-actions">
                          <label><strong>Update Status:</strong></label>
                          <div className="ad-status-buttons">
                            <button 
                              className={`ad-btn ${selectedReport.status === 'pending' ? 'ad-btn--primary' : 'ad-btn--outline'}`}
                              onClick={() => updateReportStatus(selectedReport.id, 'pending')}
                              disabled={reportStatusLoading}
                            >
                              Pending
                            </button>
                            <button 
                              className={`ad-btn ${selectedReport.status === 'reviewed' ? 'ad-btn--primary' : 'ad-btn--outline'}`}
                              onClick={() => updateReportStatus(selectedReport.id, 'reviewed')}
                              disabled={reportStatusLoading}
                            >
                              Reviewed
                            </button>
                            <button 
                              className={`ad-btn ${selectedReport.status === 'resolved' ? 'ad-btn--primary' : 'ad-btn--outline'}`}
                              onClick={() => updateReportStatus(selectedReport.id, 'resolved')}
                              disabled={reportStatusLoading}
                            >
                              Resolved
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Support Chat Tickets */}
                {analyticsTab === 'support-tickets' && (
                  <div className="ad-st-layout">
                    <div className="ad-st-sidebar">
                      {supportTickets.length === 0 ? (
                        <div className="ad-empty">No support tickets found.</div>
                      ) : (
                        supportTickets.map((t) => (
                          <button
                            key={t.id}
                            className={`ad-st-item${selectedSupportTicket?.id === t.id ? ' active' : ''}`}
                            onClick={() => fetchSupportTicketDetail(t.id)}
                          >
                            <div className="ad-st-item-top">
                              <span className="ad-st-subject">{t.subject}</span>
                              <span className={`ad-badge ad-badge--${t.status === 'open' ? 'approved' : 'denied'}`}>{t.status}</span>
                            </div>
                            <div className="ad-st-meta">
                              <span>{t.user_name} &middot; {t.user_role}</span>
                              <span>{t.message_count} msg{t.message_count !== 1 ? 's' : ''}</span>
                            </div>
                          </button>
                        ))
                      )}
                    </div>
                    <div className="ad-st-detail">
                      {!selectedSupportTicket ? (
                        <div className="ad-st-empty">Select a ticket to view conversation.</div>
                      ) : (
                        <>
                          <div className="ad-st-detail-head">
                            <div>
                              <h4 className="ad-st-detail-title">{selectedSupportTicket.subject}</h4>
                              <p className="ad-st-detail-user">{selectedSupportTicket.user_name} &middot; {selectedSupportTicket.user_email} &middot; {selectedSupportTicket.user_role}</p>
                            </div>
                            <div className="ad-st-detail-actions">
                              <span className={`ad-badge ad-badge--${selectedSupportTicket.status === 'open' ? 'approved' : 'denied'}`}>{selectedSupportTicket.status}</span>
                              {selectedSupportTicket.status === 'open' && (
                                <button className="ad-btn ad-btn--deny ad-btn--sm" onClick={() => handleCloseTicket(selectedSupportTicket.id)}>
                                  Close Ticket
                                </button>
                              )}
                            </div>
                          </div>
                          <div className="ad-st-messages">
                            {loadingTicketMsgs ? (
                              <div className="ad-loading">Loading…</div>
                            ) : ticketMessages.length === 0 ? (
                              <div className="ad-empty">No messages.</div>
                            ) : (
                              ticketMessages.map((msg) => (
                                <div key={msg.id} className={`ad-st-msg ad-st-msg--${msg.sender_type}`}>
                                  <div className="ad-st-msg-sender">{msg.sender_type === 'admin' ? 'Support (You)' : selectedSupportTicket.user_name}</div>
                                  <div className="ad-st-msg-content">{msg.content}</div>
                                  <div className="ad-st-msg-time">{msg.created_at ? new Date(msg.created_at).toLocaleString() : '—'}</div>
                                </div>
                              ))
                            )}
                          </div>
                          {selectedSupportTicket.status === 'open' && (
                            <form className="ad-st-reply-form" onSubmit={handleAdminReply}>
                              <input
                                type="text"
                                className="ad-st-reply-input"
                                placeholder="Type your reply…"
                                value={adminReplyText}
                                onChange={(e) => setAdminReplyText(e.target.value)}
                                disabled={replyLoading}
                              />
                              <button type="submit" className="ad-btn ad-btn--approve" disabled={!adminReplyText.trim() || replyLoading}>
                                {replyLoading ? 'Sending…' : 'Reply'}
                              </button>
                            </form>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                )}

                {/* Support Messages */}
                {analyticsTab === 'support' && (
                  <div className="ad-support-list">
                    {supportMessages.length === 0 ? (
                      <div className="ad-empty">No support messages found.</div>
                    ) : (
                      supportMessages.map((m) => (
                        <div className="ad-support-card" key={m.id}>
                          <div className="ad-support-header">
                            <div>
                              <p className="ad-support-name">{m.name}</p>
                              <p className="ad-support-email">{m.email}</p>
                            </div>
                            <p className="ad-support-date">
                              {m.created_at ? new Date(m.created_at).toLocaleDateString() : '—'}
                            </p>
                          </div>
                          <p className="ad-support-subject"><strong>Subject:</strong> {m.subject}</p>
                          <p className="ad-support-message">{m.message}</p>
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>

      {/* Review Modal */}
      {previewTech && (
        <div className="ad-modal-overlay" onClick={() => setPreviewTech(null)}>
          <div className="ad-modal" onClick={(e) => e.stopPropagation()}>
            <button className="ad-modal__close" onClick={() => setPreviewTech(null)}>&times;</button>
            <h2 className="ad-modal__title">Technician Review</h2>

            <div className="ad-modal__grid">
              {[
                ['Full Name', previewTech.full_name],
                ['Email', previewTech.email],
                ['Phone', previewTech.phone],
                ['Address', previewTech.address],
                ['City', previewTech.city],
                ['Province', previewTech.province],
                ['Country', previewTech.country],
                ['Trade', displayType(previewTech)],
                ['Status', previewTech.status],
                ['Registered', previewTech.created_at || '—'],
              ].map(([label, value]) => (
                <div className="ad-modal__row" key={label}>
                  <span className="ad-modal__label">{label}</span>
                  <span className={`ad-modal__value${label === 'Status' ? ` ad-badge ad-badge--${previewTech.status}` : ''}`}>
                    {value}
                  </span>
                </div>
              ))}
            </div>

            <div className="ad-modal__cert">
              <p className="ad-modal__label">Certificate / Document</p>
              {previewTech.certificate_path ? (
                <div className="ad-cert-preview">
                  {previewTech.certificate_path.match(/\.(jpg|jpeg|png)$/i) ? (
                    <img
                      src={certUrl(previewTech.certificate_path)}
                      alt="Certificate"
                      className="ad-cert-img"
                    />
                  ) : (
                    <p className="ad-cert-file">
                      PDF document &mdash;{' '}
                      <a
                        href={certUrl(previewTech.certificate_path)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="ad-cert-link"
                      >
                        Open / Download
                      </a>
                    </p>
                  )}
                </div>
              ) : (
                <p className="ad-cert-none">No certificate uploaded</p>
              )}
            </div>

            <div className="ad-modal__actions">
              {previewTech.status !== 'approved' && (
                <button
                  className="ad-btn ad-btn--approve"
                  disabled={actionLoading === previewTech.id}
                  onClick={() => handleAction(previewTech.id, 'approve')}
                >
                  Approve
                </button>
              )}
              {previewTech.status !== 'denied' && (
                <button
                  className="ad-btn ad-btn--deny"
                  disabled={actionLoading === previewTech.id}
                  onClick={() => handleAction(previewTech.id, 'deny')}
                >
                  Deny
                </button>
              )}
              <button className="ad-btn ad-btn--outline" onClick={() => setPreviewTech(null)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="ad-modal-overlay" onClick={() => setDeleteConfirm(null)}>
          <div className="ad-modal ad-modal--sm" onClick={(e) => e.stopPropagation()}>
            <h2 className="ad-modal__title">Confirm Deletion</h2>
            <p className="ad-modal__text">
              Are you sure you want to permanently delete <strong>{deleteConfirm.name}</strong>? 
              This action cannot be undone.
            </p>
            {deleteError && (
              <div className="ad-error-msg">{deleteError}</div>
            )}
            <div className="ad-modal__actions">
              <button
                className="ad-btn ad-btn--delete"
                disabled={actionLoading === deleteConfirm.id}
                onClick={() => handleDelete(deleteConfirm.id, deleteConfirm.role)}
              >
                {actionLoading === deleteConfirm.id ? 'Deleting...' : 'Delete'}
              </button>
              <button className="ad-btn ad-btn--outline" onClick={() => { setDeleteConfirm(null); setDeleteError(''); }}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminDashboard;
